import React, { useState, useEffect, useCallback, useRef } from "react";
import { getOgUserGroups, getUsers } from "apis/adminServices/userManagmentService";
import { Link } from "react-router-dom";
import UsersList from "./usersList";
import OgGroupUsers from "./ogGroupUsers";
import ManageUserRolesModal from "./manageUserRolesModal";
import { TabKey, USERS_TAB_KEY, USERS_TAB_LABEL } from "./constants";
import { OgUserGroup } from "./type";

/**
 * The tab chips, from one unscoped GetUsers. `byRole` is keyed by roleId, and a
 * group nobody is in is **absent** from it rather than zero in it — so read it
 * as `byRole[id] ?? 0`, and only once the whole object is non-null.
 */
interface TabCounts {
  /** Every user GetUsers pages, which is what the Users tab lists. */
  total: number | null;
  byRole: Record<number, number>;
}

const UserManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>(USERS_TAB_KEY);
  const [counts, setCounts] = useState<TabCounts | null>(null);
  const [ogGroups, setOgGroups] = useState<OgUserGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  // Has to be re-armed on mount, not just cleared on unmount: StrictMode mounts,
  // unmounts and remounts in dev, and a ref that only ever goes false would
  // swallow every setState from then on.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * Every chip in one call, owned by the page rather than by the open tab.
   *
   * The tabs used to hand their own GetUsers responses up, which cannot work:
   * a role tab scopes its request by `roleId`, so its response says nothing
   * about the group a user was just moved *into* — that chip stayed stale until
   * a reload. This asks unscoped (`size: 1`, the rows are not wanted) so
   * `roleCounts` covers every role whichever tab triggered it, and it is
   * deliberately unfiltered: a chip is the size of the group, not the size of
   * whatever the admin last searched for.
   */
  const refreshCounts = useCallback(async () => {
    try {
      const response = await getUsers("", "", 1, 1);
      if (!isMounted.current) return;

      const rows = response?.roleCounts ?? response?.RoleCounts;
      const byRole: Record<number, number> = {};
      if (Array.isArray(rows)) {
        rows.forEach((row: any) => {
          const roleId = Number(row?.roleId ?? row?.RoleId);
          const count = Number(row?.count ?? row?.Count);
          if (Number.isFinite(roleId) && Number.isFinite(count)) {
            byRole[roleId] = count;
          }
        });
      }

      // `totalCount` over `roleCounts.user`: the base role misses anyone
      // without it (admins), and the Users tab lists them too.
      setCounts({ total: response?.totalCount ?? null, byRole });
    } catch (error) {
      console.error("Error fetching user counts:", error);
    }
  }, []);

  /**
   * The roles themselves. Each one is a tab, so this is what the tab bar is
   * built from, and the same list is what "Move to" offers as targets.
   */
  const refreshGroups = useCallback(async () => {
    try {
      const groups = await getOgUserGroups();
      if (!isMounted.current) return;
      setOgGroups(Array.isArray(groups) ? groups : []);
    } catch (error) {
      console.error("Error fetching OG user groups:", error);
      if (isMounted.current) setOgGroups([]);
    } finally {
      if (isMounted.current) setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshGroups();
    refreshCounts();
  }, [refreshGroups, refreshCounts]);

  /**
   * A move changes two groups at once — the one the user left and the one they
   * joined — and either can be a tab that is not on screen, so both lists are
   * re-read rather than the open one patched.
   */
  const handleMembershipChange = useCallback(() => {
    refreshGroups();
    refreshCounts();
  }, [refreshGroups, refreshCounts]);

  // A role can be gone by the next refresh — fall back to Users rather than
  // leaving a tab selected that no longer has anything behind it.
  useEffect(() => {
    if (groupsLoading || activeTab === USERS_TAB_KEY) return;
    if (!ogGroups.some((group) => group.id === activeTab)) {
      setActiveTab(USERS_TAB_KEY);
    }
  }, [ogGroups, groupsLoading, activeTab]);

  const activeGroup = ogGroups.find((group) => group.id === activeTab) ?? null;

  // Counts come from `roleCounts`, never `GetOgUserGroups.userCount` — that
  // field is the group's per-role token limit, not its live membership.
  const tabs: { key: TabKey; label: string; count: number | null }[] = [
    { key: USERS_TAB_KEY, label: USERS_TAB_LABEL, count: counts?.total ?? null },
    ...ogGroups.map((group) => ({
      key: group.id as TabKey,
      label: group.roleName,
      // Absent from `roleCounts` means empty, so 0 — but only once the counts
      // have actually arrived, or every chip would flash 0 on first paint.
      count: counts ? (counts.byRole[group.id] ?? 0) : null,
    })),
  ];

  return (
    <div>
      <section className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-grease-100 tracking-tight">User Management</h1>

        <nav className="flex flex-wrap items-center text-sm text-lightgray-200 gap-x-2 gap-y-1 mt-1">
          <span className="st-dashboard text-lightgray-200 text-sm" aria-hidden="true" />
          <Link to="/admin/dashboard" className="hover:text-royalblue-100 transition-colors duration-150">
            Dashboard
          </Link>
          <span className="st-arrow-right text-lightgray-200 text-xs" aria-hidden="true" />
          <span className="text-royalblue-100 font-medium">User Management</span>
        </nav>
      </section>

      <section className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="hidden sm:block sm:w-[168px] shrink-0" aria-hidden="true" />

        <div className="w-full sm:w-auto sm:flex-1 overflow-x-auto no-scrollbar py-1">
          <div className="flex items-center gap-1.5 p-1.5 bg-blue-100 rounded-full w-max mx-auto max-w-none">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-sm font-medium text-center shrink-0 transition-all duration-150 ${
                  activeTab === tab.key
                    ? "bg-white text-royalblue-100 shadow-sm"
                    : "text-lightgray-200 hover:text-grease-100"
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span className="block whitespace-nowrap text-xs sm:text-sm">
                  {tab.label}
                </span>
                <span
                  className={`block font-semibold text-xs sm:text-sm ${
                    activeTab === tab.key
                      ? "text-royalblue-100"
                      : "text-grease-100"
                  }`}
                >
                  {tab.count ?? "-"}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Shared across every tab */}
        <button
          type="button"
          className="px-4 py-2.5 bg-[#FFBC00] rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity whitespace-nowrap shrink-0"
          onClick={() => setIsRoleModalOpen(true)}
        >
          User Role Configuration
        </button>
      </section>

      {activeGroup ? (
        // Keyed on the group so switching tabs remounts the list — no search,
        // page or selection carries over from the role you were just on.
        <OgGroupUsers
          key={activeGroup.id}
          group={activeGroup}
          groups={ogGroups}
          onMembershipChange={handleMembershipChange}
        />
      ) : (
        <UsersList groups={ogGroups} onMembershipChange={handleMembershipChange} />
      )}

      <ManageUserRolesModal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        groups={ogGroups}
        loading={groupsLoading}
        onRefresh={() => {
          refreshGroups();
          refreshCounts();
        }}
      />
    </div>
  );
};

export default UserManagement;
